import { atom, computed } from 'nanostores';
import { parseDomain, ParseResultType } from 'parse-domain';
import { loadDNSStatus } from '~/lib/.client/api/netlify';
import { chatStore } from '~/lib/stores/chat';
import { menuStore, SettingsPage } from '~/lib/stores/menu';
import type { DomainEntry, SearchDomainActionResponse } from '~/routes/api.domains.$action.($id)';

export type { DomainEntry };

export interface DomainInformation {
  domain: string;
  price: number;
  renewalPrice: number;
}

export type DomainSearchResult = SearchDomainActionResponse;

type UserDomain = {
  name: string;
  id: string;
  projectId?: string;
  projectName?: string;
};

export const steps = [
  'addCustomDomain',
  'buyNewDomain',
  'resolveDNSIssue',
  'purchaseConfirmation',
  'addDomainAlias',
  'default',
] as const;

export type Step = (typeof steps)[number];

class DomainsStore {
  modalStep = atom<Step>('default');
  modalStepDirection = atom<'back' | 'forward'>('forward');

  domainSearchQuery = atom<string>('');
  domainSearchResult = atom<DomainSearchResult | undefined>();

  domainPurchaseSelected = atom<DomainInformation | undefined>();

  alertInManageDomain = atom<string | undefined>();
  alertDismissed = atom<boolean>(false);

  dnsIssueEntry = atom<
    | { domain: string; isPrimary: boolean; isApex: true }
    | { domain: string; isPrimary: boolean; isApex: false; subdomain: string }
    | undefined
  >();

  domainEntries = atom<DomainEntry[]>([]);

  userDomainList = atom<UserDomain[]>([]);

  /**
   * Computed list of user domains enriched with project names from chat list.
   * Recomputes automatically when either userDomainList or chatStore.chatList changes.
   */
  enrichedDomains = computed([this.userDomainList, chatStore.chatList], (domains, chatList) => {
    return domains.map((domain) => {
      const chat = chatList.find((chat) => chat.projectId === domain.projectId);
      return {
        ...domain,
        projectName: chat?.description || '',
      };
    });
  });

  checkDomainAlert() {
    const entries = this.domainEntries.get();

    const hasPrimaryNotConfigured = entries.some(
      (entry) => entry.type === 'primary' && entry.status === 'not-configured',
    );

    if (hasPrimaryNotConfigured) {
      this.alertInManageDomain.set('Your custom domain has been added but is not yet functional.');
    }
  }

  setDomainAlert(text: string) {
    if (!this.alertDismissed.get()) {
      this.alertInManageDomain.set(text);
    }
  }

  clearAlert() {
    this.alertDismissed.set(true);
    this.alertInManageDomain.set(undefined);
  }

  async checkDnsStatus() {
    try {
      const domains = this.domainEntries.get();
      const entries = await loadDNSStatus(domains);

      this.domainEntries.set(entries);
      this.checkDomainAlert();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check DNS status.';
      throw new Error(errorMessage);
    }
  }

  setAddCustomDomainStep() {
    this.modalStep.set('addCustomDomain');
  }

  setBuyNewDomainStep() {
    this.modalStep.set('buyNewDomain');
  }

  setPurchaseConfirmationStep() {
    this.modalStep.set('purchaseConfirmation');
  }

  setAddDomainAliasStep() {
    this.modalStep.set('addDomainAlias');
  }

  setUserDomainList(domains: UserDomain[]) {
    this.userDomainList.set(domains);
  }

  resolveDNSIssue(entry: DomainEntry) {
    const parsedDomain = parseDomain(entry.domain);

    if (parsedDomain?.type === ParseResultType.Listed) {
      const isApex = parsedDomain.subDomains.length === 0;

      this.dnsIssueEntry.set({
        domain: entry.domain,
        isPrimary: entry.type === 'primary',
        isApex,
        subdomain: parsedDomain.icann.subDomains.join('.'),
      });
    }

    this.modalStep.set('resolveDNSIssue');
  }

  resetStep() {
    this.modalStep.set('default');
  }

  openAddCustomDomain() {
    this.setAddCustomDomainStep();
    menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Domains });
  }

  openBuyNewDomain() {
    this.setBuyNewDomainStep();
    menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Domains });
  }

  openManageDomains() {
    this.resetStep();
    this.checkDomainAlert();
    menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Domains });
  }

  openAddDomainAlias() {
    this.setAddDomainAliasStep();
    menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Domains });
  }

  resetDomainSearchQuery() {
    this.domainSearchQuery.set('');
    this.domainSearchResult.set(undefined);
  }

  resetDomainEntries() {
    this.domainEntries.set([]);
  }

  resetAll() {
    this.resetStep();
    this.resetDomainSearchQuery();
    this.resetDomainEntries();
    this.clearAlert();
    this.alertDismissed.set(false);
  }
}

export const domainsStore = new DomainsStore();

export function isValidStep(step: string | undefined): step is Step {
  return steps.includes(step as Step);
}

if (import.meta.env.DEV) {
  Object.assign(globalThis, { domainsStore });
}
